import { ROUTES } from '../../../../constants';
import { AttachingMethod, Attachment, AttachmentType } from '../../../../types';
import { logger } from '../../../util/logger';
import { httpClient } from '../../httpClient';
import { resolveUploadMimeType } from './constants';
import { MediaDimensionsProbe, MediaProbeResult } from './probeMediaDimensions';

type DynamicsUploadUrlResponse = {
  uploadUrl: string;
  filePath: string;
  expiresInSeconds?: string;
};

export class DynamicsMediaUploader {
  static upload(
    files: File[],
    attachingMethod: AttachingMethod,
    signal?: AbortSignal,
  ): Promise<Attachment[]> {
    return Promise.all(files.map((file) => new DynamicsMediaUploader(file, attachingMethod, signal).run()));
  }

  private constructor(
    private readonly file: File,
    private readonly attachingMethod: AttachingMethod,
    private readonly signal?: AbortSignal,
  ) {}

  private async run(): Promise<Attachment> {
    // Probe starts immediately and runs in parallel with both the BFF handshake and the PUT upload.
    // Results are only needed to build the Attachment after PUT completes, so probe latency is hidden.
    const probePromise = MediaDimensionsProbe.probe(this.file);
    const { uploadUrl, filePath } = await this.getUploadUrl();
    const [, probe] = await Promise.all([this.putFile(uploadUrl), probePromise]);
    return this.buildAttachment(filePath, probe);
  }

  private async getUploadUrl(): Promise<DynamicsUploadUrlResponse> {
    try {
      const { data } = await httpClient
        .getInstance()
        .post<DynamicsUploadUrlResponse>(ROUTES.MEDIA_UPLOAD_URL_API, {
          fileName: this.file.name,
          mimeType: resolveUploadMimeType(this.file),
        });
      return data;
    } catch (error) {
      logger.error({ message: 'failed to get dynamics upload url', error });
      throw error;
    }
  }

  private async putFile(uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: this.file,
      signal: this.signal,
      headers: { 'Content-Type': this.file.type },
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  private buildAttachment(filePath: string, probe: MediaProbeResult): Attachment {
    const common = {
      name: this.file.name,
      size: this.file.size,
      width: probe.width,
      height: probe.height,
      url: URL.createObjectURL(this.file),
      attachingMethod: this.attachingMethod,
      mimeType: this.file.type,
      filePath,
    };

    if (this.file.type.startsWith('image/')) {
      return { ...common, type: AttachmentType.Image };
    }

    return {
      ...common,
      type: AttachmentType.Video,
      // thumbnailUrl is a canvas-captured first frame; falls back to the raw video blob URL when
      // canvas is unavailable (e.g. tainted canvas, jsdom). Consumer must tolerate a video blob as iconUrl.
      iconUrl: probe.thumbnailUrl ?? common.url,
      duration: Math.round((probe.duration ?? 0) * 1000),
      format: probe.format ?? '',
    };
  }
}
